// Preliminary tidal-datum engine for a field gauge record. This is a simplified, from-scratch
// JS reimplementation of the ideas behind NOAA's "Computational Techniques for Tidal Datums"
// handbook (NOS CO-OPS 2) and its open-source Tidal Analysis Datum Calculator (TADC) --
// https://github.com/NOAA-CO-OPS/CO-OPS-Tidal-Analysis-Datum-Calculator -- NOT a port of that
// code, and NOT a certified NOS datum computation. Always present results as preliminary.
// Background: docs/research/tide-tool.md (task/tide-tool) section 6.

const MS_DAY = 86_400_000;

// ---- 1. Parse ---------------------------------------------------------------------------------

// Flexible two-column CSV: timestamp, water level. Header optional; accepts ISO-ish timestamps
// ("2026-01-02 03:04", "2026-01-02T03:04:00Z") or epoch seconds/millis in the first column.
export function parseGaugeCsv(text) {
  const lines = String(text || "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return [];
  const splitRow = (line) => line.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
  const first = splitRow(lines[0]);
  const hasHeader = first.some((c) => /[a-z_]/i.test(c)) && !Number.isFinite(Date.parse(first[0]));
  const body = hasHeader ? lines.slice(1) : lines;
  const out = [];
  for (const line of body) {
    const cols = splitRow(line);
    if (cols.length < 2) continue;
    const t = parseTimestamp(cols[0]);
    const v = Number(cols[1]);
    if (Number.isFinite(t) && Number.isFinite(v)) out.push({ t, v });
  }
  return out.sort((a, b) => a.t - b.t);
}

function parseTimestamp(raw) {
  const s = String(raw || "").trim();
  if (/^\d{10}$/.test(s)) return Number(s) * 1000;
  if (/^\d{13}$/.test(s)) return Number(s);
  const iso = s.includes(" ") && !s.includes("T") ? s.replace(" ", "T") : s;
  const withZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(iso) ? iso : `${iso}Z`;
  const ms = Date.parse(withZone);
  return Number.isFinite(ms) ? ms : Date.parse(s);
}

// ---- 2. QC: dedupe, drop spikes ----------------------------------------------------------------

export function qcClean(rows) {
  const byTime = new Map();
  rows.forEach((r) => byTime.set(r.t, r.v));
  const sorted = [...byTime.entries()].map(([t, v]) => ({ t, v: Number(v) })).sort((a, b) => a.t - b.t);

  // Median-absolute-deviation spike filter on the point-to-point change, not the level itself
  // (tide ranges legitimately swing meters; a single bad reading jumps far more than its
  // neighbours do).
  const diffs = sorted.slice(1).map((r, i) => Math.abs(r.v - sorted[i].v));
  const sortedDiffs = [...diffs].sort((a, b) => a - b);
  const median = sortedDiffs[Math.floor(sortedDiffs.length / 2)] ?? 0;
  const mad = sortedDiffs.length ? [...sortedDiffs].map((d) => Math.abs(d - median)).sort((a, b) => a - b)[Math.floor(sortedDiffs.length / 2)] : 0;
  const threshold = Math.max(0.5, median + 8 * (mad || 0.05));

  const clean = [sorted[0]].filter(Boolean);
  let removed = 0;
  for (let i = 1; i < sorted.length; i++) {
    const jump = Math.abs(sorted[i].v - clean[clean.length - 1].v);
    if (jump > threshold) { removed++; continue; }
    clean.push(sorted[i]);
  }

  const gaps = [];
  for (let i = 1; i < clean.length; i++) {
    const hours = (clean[i].t - clean[i - 1].t) / 3_600_000;
    if (hours > 6) gaps.push({ fromT: clean[i - 1].t, toT: clean[i].t, hours });
  }

  return {
    clean,
    removed,
    gaps,
    recordDays: clean.length ? (clean[clean.length - 1].t - clean[0].t) / MS_DAY : 0,
    startT: clean[0]?.t ?? null,
    endT: clean[clean.length - 1]?.t ?? null,
  };
}

// ---- 3. Smooth + extract hi/lo (stand-in for a Butterworth low-pass + curve fit) ---------------

function movingAverage(rows, windowMinutes) {
  if (!rows.length) return [];
  const halfMs = (windowMinutes * 60_000) / 2;
  const out = [];
  let lo = 0, hi = 0, sum = 0, n = 0;
  for (let i = 0; i < rows.length; i++) {
    const t = rows[i].t;
    while (hi < rows.length && rows[hi].t <= t + halfMs) { sum += rows[hi].v; n++; hi++; }
    while (lo < rows.length && rows[lo].t < t - halfMs) { sum -= rows[lo].v; n--; lo++; }
    out.push({ t, v: n ? sum / n : rows[i].v });
  }
  return out;
}

// Local extrema with a minimum separation so we get roughly one high/low per ~6 hour tidal
// phase, not every wiggle. minSeparationHours defaults to 4h (shorter than the ~6.2h semidiurnal
// quarter-period on purpose, so a genuine double high near a solstice/perigee isn't merged away).
export function extractHiLo(rows, { smoothingMinutes = 30, minSeparationHours = 4 } = {}) {
  const smoothed = movingAverage(rows, smoothingMinutes);
  const events = [];
  for (let i = 1; i < smoothed.length - 1; i++) {
    const [p, c, n] = [smoothed[i - 1], smoothed[i], smoothed[i + 1]];
    if (c.v >= p.v && c.v >= n.v) events.push({ t: c.t, v: c.v, type: "H" });
    else if (c.v <= p.v && c.v <= n.v) events.push({ t: c.t, v: c.v, type: "L" });
  }
  const minMs = minSeparationHours * 3_600_000;
  const out = [];
  for (const e of events) {
    const prev = out[out.length - 1];
    if (prev && prev.type === e.type && e.t - prev.t < minMs) {
      // Keep whichever is more extreme of the two close same-type events.
      if ((e.type === "H" && e.v > prev.v) || (e.type === "L" && e.v < prev.v)) out[out.length - 1] = e;
      continue;
    }
    out.push(e);
  }
  return out;
}

// ---- 4. Day-grouped datum statistics -----------------------------------------------------------

// hilo values must already be in the target vertical frame (MLLW, or the gauge's own Station
// Datum for an uncontrolled FRED run). Groups by UTC calendar day as a simplification of NOAA's
// true ~24.84h tidal day.
export function computeDayGroupedStats(hilo) {
  const byDay = new Map();
  hilo.forEach((e) => {
    const day = Math.floor(e.t / MS_DAY);
    if (!byDay.has(day)) byDay.set(day, { H: [], L: [] });
    byDay.get(day)[e.type].push(e.v);
  });
  const HH = [], LH = [], HL = [], LL = [];
  byDay.forEach(({ H, L }) => {
    if (H.length) { HH.push(Math.max(...H)); LH.push(Math.min(...H)); }
    if (L.length) { LL.push(Math.min(...L)); HL.push(Math.max(...L)); }
  });
  const mean = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);
  const allHighs = hilo.filter((e) => e.type === "H").map((e) => e.v);
  const allLows = hilo.filter((e) => e.type === "L").map((e) => e.v);
  const MHHW = mean(HH), MLLW = mean(LL), MHW = mean(allHighs), MLW = mean(allLows);
  const MTL = MHW != null && MLW != null ? (MHW + MLW) / 2 : null;
  const DTL = MHHW != null && MLLW != null ? (MHHW + MLLW) / 2 : null;
  return { MHHW, MHW, MTL, DTL, MLW, MLLW, days: byDay.size, highs: allHighs.length, lows: allLows.length };
}

// ---- 5. Tie the subordinate gauge's arbitrary zero to a control station's MLLW frame -----------

// Pairs subordinate hi/lo events with the nearest control event of the same type (within
// maxHours) and returns the mean (subordinate - control) difference -- the offset that converts
// a subordinate reading into the control's vertical frame.
export function tideByTideOffset(subHilo, ctrlHilo, maxHours = 8) {
  const byType = (arr, type) => arr.filter((e) => e.type === type).sort((a, b) => a.t - b.t);
  const pairs = [];
  for (const type of ["H", "L"]) {
    const subs = byType(subHilo, type);
    const ctrls = byType(ctrlHilo, type);
    if (!ctrls.length) continue;
    for (const s of subs) {
      let best = null, bestAbs = Infinity;
      for (const c of ctrls) {
        const absH = Math.abs(c.t - s.t) / 3_600_000;
        if (absH < bestAbs) { bestAbs = absH; best = c; }
      }
      if (best && bestAbs <= maxHours) pairs.push(s.v - best.v);
    }
  }
  if (!pairs.length) return null;
  return { offset: pairs.reduce((a, b) => a + b, 0) / pairs.length, pairCount: pairs.length };
}

// Mean-level offset over the overlapping window of two continuous (observed) series -- used for
// the Monthly Mean Simultaneous Comparison (MMSC) method.
export function monthlyMeanOffset(subRows, ctrlRows) {
  if (!subRows.length || !ctrlRows.length) return null;
  const startT = Math.max(subRows[0].t, ctrlRows[0].t);
  const endT = Math.min(subRows[subRows.length - 1].t, ctrlRows[ctrlRows.length - 1].t);
  if (endT <= startT) return null;
  const inWindow = (rows) => rows.filter((r) => r.t >= startT && r.t <= endT).map((r) => r.v);
  const subVals = inWindow(subRows), ctrlVals = inWindow(ctrlRows);
  if (!subVals.length || !ctrlVals.length) return null;
  const mean = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;
  return { offset: mean(subVals) - mean(ctrlVals), overlapDays: (endT - startT) / MS_DAY, subN: subVals.length, ctrlN: ctrlVals.length };
}

// ---- 6. Rough, illustrative accuracy heuristic (NOT NOAA's calibrated error model) --------------

// Scales a base uncertainty by record length, loosely in the spirit of Swanson (1964) / NOAA's
// regional Tidal Datum Error reports (shorter record + further/different-range control = worse),
// but with made-up constants, not fitted ones. Always label as illustrative only.
export function roughErrorEstimateMeters(method, recordDays) {
  const base = { fred: 0.12, tbyt: 0.08, mmsc: 0.05 }[method] ?? 0.1;
  const days = Math.max(1, recordDays);
  return base / Math.sqrt(days / 29.53);
}

// ---- 7. Orchestrator ----------------------------------------------------------------------------

// subordinate: { hilo, observed } raw (arbitrary zero). control: { hilo, observed, datums } in
// MLLW/metric. method: "auto" | "mmsc" | "tbyt" | "fred".
export function establishDatums({ subordinateHilo, subordinateObserved, control, method = "auto", recordDays }) {
  const hasControl = !!control?.hilo?.length;
  let chosen = method;
  if (method === "auto") {
    chosen = !hasControl ? "fred" : recordDays >= 25 && control.observed?.length ? "mmsc" : "tbyt";
  }
  if (chosen !== "fred" && !hasControl) chosen = "fred";

  if (chosen === "fred") {
    const stats = computeDayGroupedStats(subordinateHilo);
    return { method: "fred", frame: "Station Datum (your gauge's own zero -- not tied to any national datum)", offset: 0, stats, errorM: roughErrorEstimateMeters("fred", recordDays) };
  }

  let offsetInfo;
  if (chosen === "mmsc") {
    offsetInfo = monthlyMeanOffset(subordinateObserved, control.observed);
    if (!offsetInfo) chosen = "tbyt";
  }
  if (chosen === "tbyt") {
    offsetInfo = tideByTideOffset(subordinateHilo, control.hilo);
  }
  if (!offsetInfo) {
    const stats = computeDayGroupedStats(subordinateHilo);
    return { method: "fred", frame: "Station Datum (no usable overlap with the control station)", offset: 0, stats, errorM: roughErrorEstimateMeters("fred", recordDays) };
  }

  const shifted = subordinateHilo.map((e) => ({ ...e, v: e.v - offsetInfo.offset }));
  const stats = computeDayGroupedStats(shifted);
  return {
    method: chosen,
    frame: "MLLW (tied to control station, NTDE 1983-2001)",
    offset: offsetInfo.offset,
    offsetInfo,
    stats,
    errorM: roughErrorEstimateMeters(chosen, recordDays),
  };
}
